import { BoxedDeliveryIcon } from '@jouzen/ecom-components/icons/BoxedDeliveryIcon';
import { BoxedFingerIcon } from '@jouzen/ecom-components/icons/BoxedFingerIcon';
import { BoxedRingIcon } from '@jouzen/ecom-components/icons/BoxedRingIcon';
import { BoxedSizeKitIcon } from '@jouzen/ecom-components/icons/BoxedSizeKitIcon';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
} from '@jouzen/ecom-components/v3/Accordion';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import RichText from '@/app/components/RichText';
import useDisplayBestBuy from '@/app/hooks/useDisplayBestBuy';

import { tValueBestBuy, tValueSizing } from './tValues';

interface ProductSizingModalProps {
  readonly onSizeLinkClick: () => void;
}

interface HeaderProps {
  readonly children: ReactNode;
}

interface ContentProps {
  readonly children: ReactNode;
}

const HeaderText = ({ children }: HeaderProps) => {
  return (
    <Typography
      Element="h4"
      variant="h4"
      color="inherit"
      className="text-xl font-normal"
    >
      {children}
    </Typography>
  );
};

const ContentText = ({ children }: ContentProps) => {
  return (
    <div className="pb-3">
      <Typography color="inherit" Element="div" className="text-xs">
        {children}
      </Typography>
    </div>
  );
};

const MobileSizeModal = ({ onSizeLinkClick }: ProductSizingModalProps) => {
  const isBestBuyCountry = useDisplayBestBuy();
  const t = useTranslations();

  return (
    <section className="text-sandstone-500">
      <Typography
        Element="h2"
        variant="h3"
        color="inherit"
        className="pt-5 text-4xl"
      >
        {t('pdp_horizon_sizing_modal_title')}
      </Typography>
      <Typography
        Element="h3"
        variant="h4"
        color="inherit"
        className="py-3.5 text-base font-normal"
      >
        {t('pdp_size_modal_choose_the_right')}
      </Typography>

      <Accordion hiddenIndices={[isBestBuyCountry ? 0 : 3]}>
        <AccordionHeader>
          <HeaderText>{t('pdp_size_modal_size_at_home')}</HeaderText>
        </AccordionHeader>
        <AccordionContent>
          <ContentText>
            <div className="flex items-center justify-items-center pb-2 font-normal">
              <BoxedSizeKitIcon />
              <Typography
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_1_title')}
              </Typography>
            </div>
            <Typography Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_1_content')}
            </Typography>

            <div className="flex items-center justify-items-center pb-2 font-normal">
              <BoxedDeliveryIcon />
              <Typography
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_2_title')}
              </Typography>
            </div>
            <Typography Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_2_content_04_2025')}
            </Typography>

            <div className="flex items-center justify-items-center pb-2 font-normal">
              <BoxedFingerIcon />
              <Typography
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_3_title')}
              </Typography>
            </div>
            <Typography Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_3_content')}
            </Typography>

            <div className="flex items-center justify-items-center pb-2 font-normal">
              <BoxedRingIcon />
              <Typography
                Element="h5"
                variant="base"
                color="inherit"
                className="pl-2.5 text-base font-normal"
              >
                {t('pdp_size_modal_step_4_title')}
              </Typography>
            </div>
            <Typography Element="p" color="inherit" className="pb-5 pl-12">
              {t('pdp_size_modal_step_4_content')}
            </Typography>
          </ContentText>
        </AccordionContent>
        <AccordionHeader>
          <HeaderText>
            <RichText messageKey="pdp_size_modal_size_in_person_best_buy_title" />
          </HeaderText>
        </AccordionHeader>
        <AccordionContent>
          <ContentText>
            <Typography
              Element="p"
              color="inherit"
              className="py-3.5 text-base font-normal"
            >
              <RichText
                messageKey="pdp_size_modal_size_in_person_best_buy_content"
                tValues={tValueBestBuy}
              />
            </Typography>
          </ContentText>
        </AccordionContent>
      </Accordion>
      <Typography
        Element="p"
        color="inherit"
        className="py-3.5 text-base font-normal"
      >
        <RichText
          messageKey="pdp_size_modal_size_video_text_link"
          tValues={tValueSizing(onSizeLinkClick)}
        />
      </Typography>
    </section>
  );
};
export default MobileSizeModal;
